import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, KeyRound, ShieldAlert, UserRound } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/dashboard/Header";
import { Avatar } from "@/components/Avatar";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: user.name, phone: user.phone || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const forced = user.must_change_password;

  const saveProfile = useMutation({
    mutationFn: () => api.patch("/auth/me", form).then((r) => r.data),
    onSuccess: (u) => { setUser(u); qc.invalidateQueries({ queryKey: ["team"] }); toast.success("Profile updated"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const avatar = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/auth/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
    onSuccess: (u) => { setUser(u); toast.success("Profile picture updated"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const changePw = useMutation({
    mutationFn: () => api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password }).then((r) => r.data),
    onSuccess: (u) => { setUser(u); setPw({ current_password: "", new_password: "", confirm: "" }); toast.success("Password changed"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const pwMismatch = pw.confirm && pw.confirm !== pw.new_password;

  return (
    <div className="min-h-screen bg-[#FFFBEB] grain">
      <Header />
      <main className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6">
        <div className="fade-up">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-600">{user.role}</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">My profile</h1>
        </div>
        {forced && (
          <div className="flex items-start gap-3 rounded-2xl border-2 border-[#DC2626] bg-[#FEF2F2] p-4 text-sm" data-testid="force-password-banner">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-[#B91C1C]" />
            <p><strong>Set your own password to continue.</strong> Your admin gave you a one-time password. Choose a new one below — the rest of the dashboard unlocks right after.</p>
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-2">
          <section className={`brut-card brut-shadow space-y-5 p-6 ${forced ? "opacity-50 pointer-events-none" : ""}`} data-testid="profile-section">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold"><UserRound className="h-5 w-5" /> Personal information</h2>
            <div className="flex items-center gap-4">
              <Avatar user={user} size={72} />
              <div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => { if (e.target.files[0]) avatar.mutate(e.target.files[0]); e.target.value = ""; }} data-testid="avatar-file-input" />
                <button type="button" className="btn-ghost !py-2 !text-sm" onClick={() => fileRef.current?.click()} disabled={avatar.isPending} data-testid="avatar-upload-button">
                  <Camera className="h-4 w-4" /> {avatar.isPending ? "Uploading…" : "Change picture"}
                </button>
                <p className="mt-1 text-xs text-zinc-500">PNG, JPG, WEBP or GIF · under 3 MB</p>
              </div>
            </div>
            <div>
              <label className="field-label">Full name</label>
              <input className="field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} minLength={2} data-testid="profile-name-input" />
            </div>
            <div>
              <label className="field-label">Phone (WhatsApp)</label>
              <input className="field-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="98765 43210" data-testid="profile-phone-input" />
            </div>
            <div>
              <label className="field-label">Login email</label>
              <input className="field-input bg-zinc-100" value={user.email} disabled data-testid="profile-email-readonly" />
              <p className="mt-1 text-xs text-zinc-500">Your email is your login ID — ask an admin to change it.</p>
            </div>
            <button className="btn-dark w-full" onClick={() => saveProfile.mutate()} disabled={saveProfile.isPending || form.name.trim().length < 2} data-testid="profile-save-button">
              {saveProfile.isPending ? "Saving…" : "Save changes"}
            </button>
          </section>

          <section className="brut-card brut-shadow space-y-5 p-6" data-testid="password-section">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold"><KeyRound className="h-5 w-5" /> {forced ? "Set your password" : "Change password"}</h2>
            <div>
              <label className="field-label">{forced ? "One-time password from your admin" : "Current password"}</label>
              <input className="field-input" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} autoComplete="current-password" data-testid="pw-current-input" />
            </div>
            <div>
              <label className="field-label">New password (min 8 characters)</label>
              <input className="field-input" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} autoComplete="new-password" data-testid="pw-new-input" />
            </div>
            <div>
              <label className="field-label">Repeat new password</label>
              <input className={`field-input ${pwMismatch ? "!border-[#DC2626]" : ""}`} type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} autoComplete="new-password" data-testid="pw-confirm-input" />
              {pwMismatch && <p className="mt-1 text-xs font-semibold text-[#B91C1C]" data-testid="pw-mismatch">Passwords don't match.</p>}
            </div>
            <button className="btn-primary w-full" onClick={() => changePw.mutate()} disabled={changePw.isPending || !pw.current_password || pw.new_password.length < 8 || pw.new_password !== pw.confirm} data-testid="pw-submit-button">
              {changePw.isPending ? "Saving…" : forced ? "Set password & continue" : "Change password"}
            </button>
          </section>
        </div>
      </main>
    </div>
  );
}
